import { useMemo, type ReactNode } from 'react';
import { QueryCache, QueryClient, QueryClientProvider, type Query } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { logger } from '@/config/logger';
import { isMessageKey } from '@/modules/localizations';
import { actions as messagesActions } from '@/modules/messages';

export interface QueryClientProps {
    children: ReactNode;
}

const errorMessageIdOf = ({ meta }: Query<unknown, unknown>) => {
    const id = meta?.errorMessageId;

    return typeof id === 'string' && isMessageKey(id) ? id : undefined;
};

export const QueryProvider = ({ children }: QueryClientProps) => {
    const dispatch = useDispatch();

    // `onError` fires once per failed request rather than once per observer, so a list several
    // components read at the same time still reports a single failure.
    const client = useMemo(
        () =>
            new QueryClient({
                queryCache: new QueryCache({
                    onError: (error, query) => {
                        const errorMessageId = errorMessageIdOf(query);

                        if (errorMessageId) {
                            dispatch(messagesActions.displayErrorMessage({ message: { id: errorMessageId } }));
                        }

                        if (errorMessageId || query.meta?.logError === true) {
                            logger.error(error);
                        }
                    },
                }),
            }),
        [dispatch],
    );

    return (
        <QueryClientProvider client={client}>
            <>{children}</>
        </QueryClientProvider>
    );
};
